/** Map offer/conversation API shapes to UI fields (shallow copies — avoids stack overflow on populated graphs). */

export interface NormalizedOffer {
  id: string;
  _id?: string;
  amount: number;
  bidPrice: number;
  status: string;
  expiresAt?: string;
  convertedToCart?: boolean;
  initiatedBy?: 'BUYER' | 'SELLER' | string;
  conversation?: string | { id?: string; _id?: string };
  product?: {
    id?: string;
    _id?: string;
    name?: string;
    images?: string[];
    price?: number;
  };
  buyer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  createdAt?: string;
}

const MESSAGE_TYPE_MAP: Record<string, string> = {
  OFFER_CREATED: 'BID_PROPOSAL',
  OFFER_COUNTER: 'COUNTER_OFFER',
  OFFER_ACCEPTED: 'BID_ACCEPTED',
  OFFER_REJECTED: 'BID_REJECTED',
  OFFER_CANCELLED: 'BID_REJECTED',
  OFFER_EXPIRED: 'BID_REJECTED',
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const normalizeOffer = (
  offer: Record<string, unknown> | null | undefined
): NormalizedOffer | null => {
  if (!isPlainObject(offer)) return null;
  const id = String(offer.id || offer._id || '');
  if (!id) return null;
  const amount = Number(offer.amount ?? offer.bidPrice ?? 0);
  const conversation = offer.conversation;
  let conversationRef: NormalizedOffer['conversation'];
  if (typeof conversation === 'string') {
    conversationRef = conversation;
  } else if (isPlainObject(conversation)) {
    const cid = String(conversation.id || conversation._id || '');
    conversationRef = cid ? { id: cid, _id: cid } : undefined;
  }

  const product = offer.product;
  let productRef: NormalizedOffer['product'];
  if (typeof product === 'string') {
    productRef = { id: product, _id: product };
  } else if (isPlainObject(product)) {
    const pid = String(product.id || product._id || '');
    productRef = {
      id: pid,
      _id: pid,
      name: product.name as string | undefined,
      images: Array.isArray(product.images) ? (product.images as string[]) : undefined,
      price: product.price as number | undefined,
    };
  }

  const buyer = offer.buyer;
  let buyerRef: NormalizedOffer['buyer'];
  if (isPlainObject(buyer)) {
    buyerRef = {
      firstName: buyer.firstName as string | undefined,
      lastName: buyer.lastName as string | undefined,
      email: buyer.email as string | undefined,
    };
  }

  return {
    id,
    _id: String(offer._id || id),
    amount,
    bidPrice: amount,
    status: String(offer.status || 'PENDING'),
    expiresAt: offer.expiresAt as string | undefined,
    convertedToCart: Boolean(offer.convertedToCart ?? offer.status === 'CONVERTED'),
    initiatedBy: (offer.initiatedBy as string) || 'BUYER',
    conversation: conversationRef,
    product: productRef,
    buyer: buyerRef ?? null,
    createdAt: offer.createdAt as string | undefined,
  };
};

export const getSenderId = (msg: Record<string, unknown> | null | undefined): string | null => {
  if (!msg) return null;
  if (msg.senderId) return String(msg.senderId);
  const sender = msg.sender;
  if (!sender) return null;
  if (typeof sender === 'string') return sender;
  if (isPlainObject(sender)) {
    return String(sender.id || sender._id || '') || null;
  }
  return null;
};

export const normalizeMessage = (msg: Record<string, unknown>) => {
  const rawOffer =
    isPlainObject(msg.offer) ? msg.offer : isPlainObject(msg.bid) ? msg.bid : null;
  const offer = normalizeOffer(rawOffer);
  const type = MESSAGE_TYPE_MAP[String(msg.type)] || String(msg.type);
  const text = String(msg.text ?? msg.message ?? '');

  return {
    id: String(msg.id || msg._id || ''),
    _id: String(msg._id || msg.id || ''),
    type,
    text,
    message: text,
    createdAt: msg.createdAt as string | undefined,
    readAt: msg.readAt as string | undefined,
    senderId: getSenderId(msg),
    offer,
    bid: offer,
  };
};

export const normalizeMessages = (payload: unknown) => {
  if (!payload) return [];
  let list: unknown[] = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (isPlainObject(payload) && Array.isArray(payload.messages)) {
    list = payload.messages;
  } else if (isPlainObject(payload)) {
    const nested = payload.data;
    if (isPlainObject(nested) && Array.isArray(nested.messages)) {
      list = nested.messages as unknown[];
    }
  }
  const normalized = list
    .map((m) => normalizeMessage(m as Record<string, unknown>))
    .filter(Boolean);
  return normalized.length > 500 ? normalized.slice(-500) : normalized;
};

export const getOfferAmount = (offer: { amount?: number; bidPrice?: number } | null) =>
  offer?.amount ?? offer?.bidPrice ?? 0;

export const getConversationId = (
  source: { conversation?: string | { id?: string; _id?: string } } | null | undefined
) => {
  if (!source?.conversation) return '';
  if (typeof source.conversation === 'string') return source.conversation;
  return source.conversation.id || source.conversation._id || '';
};

export const getProductId = (
  source: { product?: string | { id?: string; _id?: string } } | null | undefined
) => {
  if (!source?.product) return '';
  if (typeof source.product === 'string') return source.product;
  return source.product.id || source.product._id || '';
};
